import { Header } from 'common/ui/Header';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import { Paths } from 'services/navigation/paths';
import Yup from 'utils/validation';
import { useFormik } from 'formik';
import { CampaignCreationNextButton } from 'modules/campaign-creation/components/CampaignCreationNextButton';
import { RadioGroup } from 'common/form';
import { Stack } from '@mui/material';
import { CampaignCreationStoreState } from 'modules/campaign-creation/stores/CampaignCreationStore';
import {
  useIntegration,
  useResolvedIntegration
} from 'services/integration/hooks';
import { LoadingIndicator } from 'common/ui/LoadingIndicator';
import { ATS_LOOKUP_LOADING_TITLE } from 'services/integration/constants';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import { checkCampaignIsForAnalysis } from 'modules/campaign-creation/hooks/useCheckCampaignIsForAnalysis';
import { useSelectedCandidateBatchStatusesHandler } from 'modules/campaign-creation/hooks/useSelectedCandidateBatchStatusesHandler';
import { useEffect } from 'react';
import { getTitle } from 'modules/campaign-creation/utils/getTitle';
import { getChooseRecipientsConfig } from 'modules/campaign-creation/utils/getChooseRecipientsConfig';

export type RecipientsType = 'everyone' | 'specific' | 'imported';

export const getRecipientsType = (
  state: CampaignCreationStoreState | null,
  selectedCandidateBatchStatuses: string[]
): RecipientsType => {
  const isCampaignForAnalysis = checkCampaignIsForAnalysis(
    selectedCandidateBatchStatuses
  );

  if (isCampaignForAnalysis) {
    return 'specific';
  }

  if (state?.ATSImportState?.atsListId || state?.CSVImportState?.csvS3Key) {
    return 'imported';
  }

  return 'everyone';
};

export const CampaignCreationChooseRecipientsStep: React.FC = () => {
  const { isListsAvailable } = useIntegration();
  const { isIntegrationResolved } = useResolvedIntegration();
  const state = useCampaignCreationStore((store) => store.state);
  const onChooseRecipientsSubmit = useCampaignCreationStore(
    (store) => store.onChooseRecipientsSubmit
  );
  const onChooseRecipientsBack = useCampaignCreationStore(
    (store) => store.onChooseRecipientsBack
  );
  const { openModal } = useModalV2();
  const { candidateBatchStatuses, onSelectEveryone, onSelectImport } =
    useSelectedCandidateBatchStatusesHandler();

  const recipientType = getRecipientsType(state, candidateBatchStatuses);
  const { label, options } = getChooseRecipientsConfig(state, isListsAvailable);

  const formik = useFormik({
    initialValues: {
      recipientType: recipientType === 'everyone' ? '' : recipientType
    },
    validationSchema: Yup.object({
      recipientType: Yup.string().required('Choose an option.')
    }),
    onSubmit: (values) => {
      if (values.recipientType === 'everyone') {
        openModal({
          title: 'Send to everyone who applies?',
          subtitle:
            'Everyone who applies to this job will receive this campaign, including rejected candidates.\n\nChoose "Specific applicants in an analysis" if you only want to send this campaign to candidates you want to progress.',
          onPrimaryAction: () => onSelectEveryone()
        });
        return;
      }

      if (values.recipientType === 'specific') {
        onChooseRecipientsSubmit({
          recipientType: 'specific'
        });
        return;
      }

      onSelectImport();
    }
  });

  useEffect(() => {
    if (
      recipientType === 'specific' &&
      formik.values.recipientType !== 'specific'
    ) {
      formik.setFieldValue('recipientType', 'specific');
    }
  }, [recipientType]);

  // Wait for the ATS lookup before offering options derived from it. Placed after
  // the hooks above, not with the other early returns, so the hook order stays
  // the same across the unresolved and resolved renders.
  //
  // `isListsAvailable` reads false while the answer is in flight, so rendering
  // straight away builds the option list from "no ATS" and then rebuilds it —
  // and anyone quick enough submits against the wrong set.
  if (!isIntegrationResolved) {
    return <LoadingIndicator title={ATS_LOOKUP_LOADING_TITLE} />;
  }

  return (
    <>
      <Header
        title="Choose recipients"
        breadcrumbs={[{ name: 'Campaigns', path: Paths.CAMPAIGNS }]}
        currentBreadcrumb={getTitle(state)}
        onBack={() => onChooseRecipientsBack()}
      />
      <Stack spacing="40px">
        <RadioGroup
          field={{
            label,
            options
          }}
          error={!!formik.errors.recipientType}
          errorMessage={formik.errors.recipientType}
          value={formik.values.recipientType}
          onChange={(e) => {
            formik.setFieldValue('recipientType', e as RecipientsType);
          }}
        />
        <CampaignCreationNextButton handleNext={() => formik.handleSubmit()} />
      </Stack>
    </>
  );
};
